/**
 * SendRateReporter — periodic per-message-type send-rate snapshot to
 * the remote log pipe (gameLogger.warn → RemoteTransport → /api/logs →
 * logs/client.log on the server).
 *
 * Why: NetobsPacketSparkline shows aggregate OUT bytes/msgs but doesn't
 * answer "which message type is at 234 m/s?" — that breakdown is in
 * NetworkTracer.outboundMessageTypeStats but only available in the
 * browser. This reporter forwards the deltas remotely so server-side
 * triage doesn't require the user to paste a console command.
 *
 * Cadence: 2s. Each tick logs the top-5 outbound types by delta-count.
 * Idle players (no sends) emit nothing — keeps the log file lean.
 *
 * Gating: isNetobsEnabled() — same gate as the visible netobs panels.
 * Off by default; opt-in via `?netobs=1` or `?debug=1`.
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import { gameLogger } from '../../../utils/GameLogger';

const REPORT_INTERVAL_MS = 2000;

interface PerTypeSnapshot {
  count: number;
  totalBytes: number;
}

const lastByType = new Map<string, PerTypeSnapshot>();

function tick(): void {
  const tracer = (globalThis as any).nm?.tracer;
  if (!tracer || !(tracer.outboundMessageTypeStats instanceof Map)) return;

  const deltas: Array<{ type: string; deltaCount: number; deltaBytes: number; avgBytes: number }> = [];
  for (const [type, stats] of tracer.outboundMessageTypeStats as Map<string, { count: number; totalBytes: number; avgBytes: number }>) {
    const prev = lastByType.get(type);
    const deltaCount = stats.count - (prev?.count ?? 0);
    const deltaBytes = stats.totalBytes - (prev?.totalBytes ?? 0);
    lastByType.set(type, { count: stats.count, totalBytes: stats.totalBytes });
    if (deltaCount > 0) {
      deltas.push({ type, deltaCount, deltaBytes, avgBytes: stats.avgBytes });
    }
  }

  if (deltas.length === 0) return;

  deltas.sort((a, b) => b.deltaCount - a.deltaCount);
  const top = deltas.slice(0, 5);
  const totalDeltaCount = deltas.reduce((sum, d) => sum + d.deltaCount, 0);
  const totalDeltaBytes = deltas.reduce((sum, d) => sum + d.deltaBytes, 0);
  const windowSecs = REPORT_INTERVAL_MS / 1000;

  gameLogger.warn('netobs', '[send-rate]', {
    windowSecs,
    totalMsgsPerSec: Math.round(totalDeltaCount / windowSecs),
    totalBytesPerSec: Math.round(totalDeltaBytes / windowSecs),
    top: top.map((d) => ({
      type: d.type,
      msgsPerSec: Math.round(d.deltaCount / windowSecs),
      bytesPerSec: Math.round(d.deltaBytes / windowSecs),
      avgBytes: d.avgBytes,
    })),
  });
}

const SendRateReporterInner: React.FC = () => {
  useEffect(() => {
    const id = setInterval(tick, REPORT_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
  return null;
};

export const SendRateReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <SendRateReporterInner />;
};
